import { createSlice } from "@reduxjs/toolkit";
import { fetchProductBundleDetails } from "Pages/marketplace/Services/ProductBundleThunk";
import { BUNDLE_API_ERROR_MESSAGE, isBundleDurationVisible } from "Pages/marketplace/Utils/BundleHelper";
import { setProp } from "Store/Actions";

export type PaymentOption = 'pay_now' | 'pay_later';
export type PaymentOutcome = 'successful' | 'failed' | 'cancelled' | 'pending';

export interface StripeConfigState {
  enabled: boolean;
  mode: 'test' | 'live';
  currency: string;
  publishableKey: string | null;
}

const initialData = {
  marketplace_sid: undefined,
  products: [],
  // Bundle of products
  productBundle: {
    loading: false,
    selectedEdgeMxieServer: "",
    bundleComponents: [],
    configuratorFields: {},
    prerequisites: [],
    automaticAdditions: [],
    haveMxieServer: 'yes',
    mxieEdgeServers: [],
    productDescription: "",
    selectedDuration: 1,
    additionalItems: [],
    product_details: {},
    toggleCrosserPackageSelection: false,
    errorMessage: null
  },
  productTypes: [],
  productSubtypes: [],
  productSubCategoriesList: [],
  partnerList: [],
  customerList: [],
  networkList: [],
  countryList: [],
  accountDropdown: [],
  isPartnerLoading: false,
  isCountryLoading: false,
  isAccountLoading: false,
  isCustomerLoading: false,
  isProductTypeLoading: false,
  isProductSubTypeLoading: false,
  isProductSubCategoryLoading: false,
  isGetProducts: false,
  orderResponseProps: {
    isOrderLoading: false,
    isOrderCreated: false,
    isOrderErrorOccur: false,
  },
  isTermsConditionsChecked: false,
  isSelectedProductLoading: false,
  isProductsLoading: false,
  isNetworkSliceLoading: false,
  networkSliceError: null,
  showFilter: false,
  filters: {
    selection: {
      productType: [],
      productSubType: [],
      productSubCategory: [],
      nokiaTested: [],
      partner: "",
      customer: "",
      network: "",
      shippingCountry: "",
      billingCountry: "",
      shipTo: "",
      searchText: "",
      productPortfolio: [],
      productSortyby: "",
      onlyDeliverable: true,
      targetSalesMode: "",
      scrmID: ""
    },
  },
  cart: [],
  cartUuid: null, // Store UUID for cart session
  selectedDeviceID: "",
  selectedDeviceSpecification: [],
  accountDetails: [],
  quotationResponse: {},
  quotations: [],
  quotationRefernce: '',
  userAccessInfo: [],
  uploadPODetails: {
    poReferenceNumber: "",
    uploadedFile: [],
    billingAddress: {},
    shippingAddress: {}
  },
  activeOrders: [],
  nokiaTermsConditions: "",
  showMerchantProducts: true,
  baseQuery: "",
  productQueryFilter: "",
  shippingCountryText: "",
  isCartChanged: false,
  isCartLoading: false,
  waterfallPricelistError: null as string | null,
  showShipTo: false,
  cartResponse: {
    currency: "",
    cartSummary: {},
    cartOfferId: "",
    waterfallPricing: null,
  },
  orderType: {
    orderNow: false,
    generateQuote: false
  },
  savedCartInfo: {},
  isNokiaUser: false,
  persona: "",
  totalProductsCount: 0,
  agreementDetails: {
    agreements: [],
    isAgreementValid: false,
    validationResult: {}
  },
  orderQuatotionLoading: false,
  columnFilter: {
    offer_id: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    name: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    endCustomerName: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    offer_created_date: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    cost: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    owner_email: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    status: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    },
    po_link_pdf_attachment_link: {
      filter: "",
      sort: "",
      checkempty: false,
      filterCheck: false
    }
  },
  termsAcceptance: {
    isAcceptedTermOne: false,
    isAcceptedTermTwo: false,
  },
  additionalInputs: {
    adminEmail: "",
    technicalContact: "",
    selectedNetworkId: "",
    selectedSliceId: "",
    manualSliceId: "",
    isManualSliceInput: false,
    applicationAdminEmail: "",
  },
  orderFormFileData: {},
  merchantCustomerRules: [],

  // Stripe payment POC
  paymentOption: null,
  stripeSessionId: null,
  stripeOfferId: null as string | null,
  paymentOutcome: null,
  stripeConfig: null,
  pendingStripeReturn: false,
  stripeRedirectHint: null as string | null,
  
};

const marketplaceSlice = createSlice({
  name: 'marketplace',
  initialState: initialData,
  reducers: {

    addProductToCart(state, action) {
      const { productToAdd, quantity, fromSpecification } = action.payload;

      // Find the index of the product if it exists in the cart
      const existingProductIndex = state.cart.findIndex(
        (item) => item.id === productToAdd.id
      );
      if (existingProductIndex !== -1) {
        if (fromSpecification) {
          // Set the quantity from the specification page
          state.cart[existingProductIndex].product_quantity = quantity;
        } else {
          // Increment by one if added from catalogue
          state.cart[existingProductIndex].product_quantity += 1;
        }
        // Recalculate the total cost based on the new quantity
        const newTotalCost = state.cart[existingProductIndex].product_quantity * parseInt(state.cart[existingProductIndex].price_amount);
        state.cart[existingProductIndex].product_total_cost = newTotalCost;
      } else {
        // If the product does not exist, add it to the cart with the specified quantity
        state.cart.push({
          ...productToAdd,
          product_quantity: quantity || 1 // Default to 1 if no quantity specified
        });
      }
    },
    // For adding multiple products at once
    addMultipleProductToCart(state, action) {
      const { products } = action.payload;
      products.forEach((productToAdd) => {
        const { quantity = 1, fromSpecification } = productToAdd;

        // Find the index of the product if it exists in the cart
        const existingProductIndex = state.cart.findIndex(
          (item) => item.id === productToAdd.id
        );

        if (existingProductIndex !== -1) {
          if (fromSpecification) {
            // Set the quantity from the specification page
            state.cart[existingProductIndex].product_quantity = quantity;
          } else {
            // Increment by the specified quantity if added from catalogue
            state.cart[existingProductIndex].product_quantity += quantity;
          }
          // Recalculate the total cost based on the new quantity
          const newTotalCost = state.cart[existingProductIndex].product_quantity * parseInt(state.cart[existingProductIndex].price_amount);
          state.cart[existingProductIndex].product_total_cost = newTotalCost;
        } else {
          // If the product does not exist, add it to the cart with the specified quantity
          state.cart.push({
            ...productToAdd,
            product_quantity: quantity
          });
        }
      });
    },

    removeProductFromCart(state, action) {
      const productIdToRemove = action.payload;
      state.cart = state.cart.filter(item => item.id !== productIdToRemove);
      // Check if the cart is empty and reset cartResponse if true
      if (state.cart.length === 0) {
        state.cartResponse = initialData.cartResponse
      }
    },
    // Remove multiple products from cart
    removeMultipleProductsFromCart(state, action) {
      const productIdsToRemove = action.payload;
      const idsToRemoveSet = new Set(productIdsToRemove);
      state.cart = state.cart.filter(item => !idsToRemoveSet.has(item.id));
      // Check if the cart is empty and reset cartResponse if true
      if (state.cart.length === 0) {
        state.cartResponse = initialData.cartResponse
      }
    },
    clearCart(state,) {
      state.cart = []
    },
    setProducts(state, action) {
      let data = action.payload
      state.products = data
    },
    // To set the bundle of product items (supports single or multiple keys)
    setBundleOfProductItems(state, action) {
      const payload = action?.payload;

      // Check if payload is an array and process all key-value pairs
      if (payload?.key && payload?.value !== undefined) {
        state.productBundle[payload.key] = payload.value;
      }
      // Otherwise, treat payload as an object with multiple key-value pairs
      else if (typeof payload === 'object' && payload !== null) {
        Object.keys(payload).forEach(key => {
          state.productBundle[key] = payload[key];
        });
      }
    },
    setBundleOfProductDetails(state, action) {
      const payload = action?.payload;
      if (payload?.key && payload?.value !== undefined) {
        state.productBundle.product_details[payload.key] = payload.value;
      }
    },
    // Tp set configuratorFields inside productBundle
    setConfiguratorFields(state, action) {
      state.productBundle["configuratorFields"] = action?.payload?.configuratorFields;
    },
    setProductTypes(state, action) {
      const data = action.payload.productTypes;
      state.productTypes = data;
    },
    setProductSubTypes(state, action) {
      const data = action.payload.productSubTypes;
      state.productSubtypes = data;
    },
    setProductSubCategoriesList(state, action) {
      const data = action.payload.productSubCategoriesList;
      state.productSubCategoriesList = data;
    },
    setCountryList(state, action) {
      const data = action.payload;
      state.countryList = data;
    },
    setPartnerList(state, action) {
      state.partnerList = action.payload.partnerList
    },
    setCustomerList(state, action) {
      const { customerList } = action.payload;
      state.customerList = customerList;
    },
    setNetworkList(state, action) {
      const { networkList } = action.payload;
      state.networkList = networkList;
    },
    setAccountDropdown(state, action) {
      const { accountDropdown } = action.payload;
      state.accountDropdown = accountDropdown;
    },
    setLoader(state, action) {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setFilters(state, action) {
      const { filterName, value } = action.payload;
      state.filters.selection[filterName] = value;
    },
    setShowFilter(state, action) {
      state.showFilter = action.payload
    },
    setSelectedDeviceID(state, action) {
      state.selectedDeviceID = action.payload
    },
    setSelectedDeviceSpecification(state, action) {
      state.selectedDeviceSpecification = action.payload.selectedProduct
    },
    setQuotationRefernce(state, action) {
      state.quotationRefernce = action.payload
    },
    setQuotations(state, action) {
      state.quotations = action.payload
    },
    updateCartProduct: (state, action) => {
      const { itemId, updatedSelectedData } = action.payload;
      const updatedItems = state.cart.map(item =>
        item.id === itemId ? { ...item, ...updatedSelectedData } : item
      );

      state.cart = updatedItems;
    },
    setUserAccessInfo(state, action) {
      const userData = action.payload;
      state.userAccessInfo = userData
    },
    setAccountDetails(state, action) {
      const accountData = action.payload;
      state.accountDetails = accountData
    },
    setQuotationResponse(state, action) {
      const quotationResponse = action.payload;
      state.quotationResponse = quotationResponse;
    },
    setOrderResponseProps(state, action) {
      state.orderResponseProps = action.payload;
    },
    updatePOReferenceNumber: (state, action) => {
      state.uploadPODetails.poReferenceNumber = action.payload;
    },
    updateUploadedFile: (state, action) => {
      state.uploadPODetails.uploadedFile = action.payload;
    },
    updateBillingAddress: (state, action) => {
      state.uploadPODetails.billingAddress = action.payload;
    },
    updateShippingAddress: (state, action) => {
      state.uploadPODetails.shippingAddress = action.payload;
    },
    clearUploadPO: (state) => {
      state.uploadPODetails = initialData.uploadPODetails;
      state.orderType = initialData.orderType;
      state.quotationRefernce = initialData.quotationRefernce;
      state.orderFormFileData = initialData.orderFormFileData;
    },
    setActiveOrders(state, action) {
      state.activeOrders = action.payload
    },
    setProductQueryFilter(state, action) {
      state.productQueryFilter = action.payload
    },
    updateEntireCart(state, action) {
      state.cart = action.payload
    },
    resetOrderResponseProps(state) {
      state.orderResponseProps = initialData.orderResponseProps
      state.orderType = initialData.orderType
      state.paymentOption = initialData.paymentOption;
      state.stripeSessionId = initialData.stripeSessionId;
      state.stripeOfferId = initialData.stripeOfferId;
      state.paymentOutcome = initialData.paymentOutcome;
      state.pendingStripeReturn = initialData.pendingStripeReturn;
      state.stripeRedirectHint = initialData.stripeRedirectHint;
    },
    setStripeRedirectHint(state, action) {
      state.stripeRedirectHint = action.payload;
    },
    resetMarketplaceFilters(state) {
      // const currentPartner = state.filters.selection.partner;
      // const currentBillingCountry = state.filters.selection.billingCountry
      // state.filters = {
      //   ...initialData.filters,
      //   selection: {
      //     ...initialData.filters.selection,
      //     partner: currentPartner,
      //     billingCountry:currentBillingCountry
      //   },
      // };
      state.filters = initialData.filters
      state.accountDetails = initialData.accountDetails
      state.products = initialData.products
      state.productSubtypes = initialData.productSubtypes
      state.productSubCategoriesList = initialData.productSubCategoriesList
      state.productTypes = initialData.productTypes
      state.showMerchantProducts = initialData.showMerchantProducts
      state.productQueryFilter = initialData.productQueryFilter
      state.cartResponse = initialData.cartResponse
      state.savedCartInfo = initialData.savedCartInfo
      state.selectedDeviceID = initialData.selectedDeviceID
      state.selectedDeviceSpecification = initialData.selectedDeviceSpecification
      state.productQueryFilter = initialData.productQueryFilter
      state.showShipTo = initialData.showShipTo
      state.agreementDetails = initialData.agreementDetails
    },
    resetCartResponse(state) {
      state.cartResponse = initialData.cartResponse
    },
    setUserPersona(state, action) {
      state.persona = action.payload
    },
    setAgreementDetails: (state, action) => {
      state.agreementDetails.agreements = action.payload.agreements;
      state.agreementDetails.isAgreementValid = action.payload.isAgreementValid;
      if (action?.payload?.validationResult) {
        state.agreementDetails.validationResult = action?.payload?.validationResult;
      }
    },
    setAgreementValidity: (state, action) => {
      state.agreementDetails.isAgreementValid = action.payload;
    },
    removeProductItem: (state, action) => {
      const { productToAdd, quantity, fromSpecification } = action.payload;
      // Find the index of the product if it exists in the cart
      const existingProductIndex = state.cart.findIndex(
        (item) => item.id === productToAdd.id
      );
      if (existingProductIndex !== -1) {
        if (fromSpecification) {
          // Set the quantity from the specification page
          state.cart[existingProductIndex].product_quantity = quantity;
        } else {
          // Decrement by one if added from catalogue
          state.cart[existingProductIndex].product_quantity -= 1;
          action.payload.quantity = state.cart[existingProductIndex].product_quantity;
        }
        // Recalculate the total cost based on the new quantity
        const newTotalCost = state.cart[existingProductIndex].product_quantity * parseInt(state.cart[existingProductIndex].price_amount);
        state.cart[existingProductIndex].product_total_cost = newTotalCost;
      }
    },

    // Change the bundle packages selection
    setCrosserPackagesSelection(state, action) {
      const { productId, packageName } = action.payload;
      const product = state.products.find((item) => item.id === productId);
      if (product?.kit_products) {
        product?.kit_products.forEach((pkg) => {
          pkg.checked = pkg?.name?.toLowerCase() === packageName?.toLowerCase();
        });
        product.price_amount = product?.kit_products?.find(pkg => pkg.checked)?.price_amount;
        for (const kitProduct of product?.kit_products) {

          if (kitProduct?.name?.toLowerCase() !== packageName?.toLowerCase()) {
            continue;
          }

          const durationVisible = isBundleDurationVisible(
          state?.productBundle?.product_details?.['maximum_duration']
            ? state.productBundle.product_details
            : product
          );
          if (kitProduct?.name?.toLowerCase() === packageName?.toLowerCase()) {
              if (state?.productBundle?.product_details?.['maximum_duration'] && state?.productBundle?.product_details?.['minimum_duration'] && state?.productBundle?.product_details?.['step_size']) {
              state.productBundle.product_details['maximum_duration'] = kitProduct?.maximum_duration;
              state.productBundle.product_details['minimum_duration'] = kitProduct?.minimum_duration;
              state.productBundle.product_details['step_size'] = kitProduct?.step_size;
              //state.productBundle.product_details['selectedDuration'] = kitProduct?.minimum_duration;
              state.productBundle.product_details['mxie_required'] = kitProduct?.mxie_required;
              // show_duration is NOT copied from selected kit — fixed at bundle load from kit_products[0]
              state.productBundle.product_details['selectedDuration'] = durationVisible
                ? kitProduct?.minimum_duration
                : 1;
            }else{
                product['maximum_duration'] = kitProduct?.maximum_duration;
                product['minimum_duration'] = kitProduct?.minimum_duration;
                product['step_size'] = kitProduct?.step_size;
                product['mxie_required'] = kitProduct?.mxie_required;
                // show_duration is NOT copied from selected kit — fixed at bundle load from kit_products[0]
                product['selectedDuration'] = durationVisible
                  ? kitProduct?.minimum_duration
                  : 1;
            }
          }
        }
      }
      state.productBundle.toggleCrosserPackageSelection = !state.productBundle.toggleCrosserPackageSelection;
    },
    setTermsAcceptance(state, action) {
      const { term, isAccepted } = action.payload;
      if (term === "isAcceptedTermOne") {
        state.termsAcceptance.isAcceptedTermOne = isAccepted;
      } else if (term === "isAcceptedTermTwo") {
        state.termsAcceptance.isAcceptedTermTwo = isAccepted;
      }
    },
    setAdditionalInputs(state, action) {
      const { field, value } = action.payload;
      state.additionalInputs[field] = value;
    },
    updateAdditionalInputs(state, action) {
      state.additionalInputs = {
        ...state.additionalInputs,
        ...action.payload
      };
    },
    clearAdditionalInputs(state) {
      state.additionalInputs = {
        adminEmail: "",
        technicalContact: "",
        selectedNetworkId: "",
        selectedSliceId: "",
        manualSliceId: "",
        isManualSliceInput: false,
        applicationAdminEmail: "",
      };
      state.termsAcceptance.isAcceptedTermOne = false;
      state.termsAcceptance.isAcceptedTermTwo = false;
    },
    setMarketplaceSid(state, action) {
      state.marketplace_sid = action.payload;
    },
    updateMxieEdgeServer(state, action) {
      const { product_code, mxieEdgeServer } = action.payload;
      if (state.productBundle && Array.isArray(state.productBundle.mxieEdgeServers)) {
        state.productBundle.mxieEdgeServers = state.productBundle.mxieEdgeServers.map(server =>
          server.product_code === product_code
            ? { ...server, ...mxieEdgeServer }
            : server
        );
      }
    },

    // --- Stripe payment POC reducers ---
    setPaymentOption(state, action) {
      state.paymentOption = action.payload;
    },
    setStripeSessionId(state, action) {
      state.stripeSessionId = action.payload;
    },
    setStripeOfferId(state, action) {
      state.stripeOfferId = action.payload;
    },
    setPaymentOutcome(state, action) {
      state.paymentOutcome = action.payload;
    },
    setStripeConfig(state, action) {
      state.stripeConfig = action.payload;
    },
    setPendingStripeReturn(state, action) {
      state.pendingStripeReturn = action.payload;
    },
    resetPaymentState(state) {
      state.paymentOption = initialData.paymentOption;
      state.stripeSessionId = initialData.stripeSessionId;
      state.stripeOfferId = initialData.stripeOfferId;
      state.paymentOutcome = initialData.paymentOutcome;
      state.pendingStripeReturn = initialData.pendingStripeReturn;
      state.stripeRedirectHint = initialData.stripeRedirectHint;
    },
  },
  extraReducers(builder) {
    builder.addCase(setProp, (state, action: {
      payload: any;
      type: string;
      slice: string;
    }) => {
      if (action.payload.slice === marketplaceSlice.name) {
        state[action.payload.key] = action.payload.value;
      }
    })
      // fetchProductBundleDetails
      .addCase(fetchProductBundleDetails.pending, (state) => {
        state.productBundle.loading = true;
        state.productBundle.errorMessage = null;
      })
      .addCase(fetchProductBundleDetails.fulfilled, (state, action) => {
        if (action?.payload?.["bundleComponents"]) {
          // Update the bundle components in the state if they exist in the payload
          state.productBundle.bundleComponents = (action?.payload?.["bundleComponents"] as []) || [];
        }
        if (action?.payload?.["prerequisites"]) {
          // Update the prerequisites in the state if they exist in the payload
          state.productBundle.prerequisites = (action?.payload?.["prerequisites"] as []) || [];
        }
        // if (action?.payload?.["automatic_additions"]) {
        //   // Update the automatic additions in the state if they exist in the payload
        //   state.productBundle.automaticAdditions = (action?.payload?.["automatic_additions"] as []) || [];
        // }
        if (action?.payload?.["productDescription"]) {
          state.productBundle.productDescription = (action?.payload?.["productDescription"] as string) || '';
        }
        if (action?.payload?.["mxieEdgeServers"]) {
          // Update the MXIE Edge Servers in the state if they exist in the payload
          state.productBundle.mxieEdgeServers = (action?.payload?.["mxieEdgeServers"] as []) || [];
        }
        if (action?.payload?.["configuratorFields"]) {
          state.productBundle.configuratorFields = (action?.payload?.["configuratorFields"] as {}) || {};
        }
        state.productBundle.loading = false;
        state.productBundle.errorMessage = null;
      })
      .addCase(fetchProductBundleDetails.rejected, (state) => {
        state.productBundle.loading = false;
        state.productBundle.errorMessage = BUNDLE_API_ERROR_MESSAGE.fetchBundleDetails;
      });
  }
})

export default marketplaceSlice.reducer;             //setProductSubTypes
export const {
  addProductToCart,
  removeProductFromCart,
  clearCart,
  setProductTypes,
  setPartnerList,
  setLoader,
  setProducts,
  setFilters,
  setUserAccessInfo,
  updateCartProduct,
  setShowFilter,
  setSelectedDeviceID,
  setSelectedDeviceSpecification,
  setQuotationRefernce,
  setQuotations,
  setAccountDetails,
  setQuotationResponse,
  setProductQueryFilter,
  updatePOReferenceNumber,
  updateUploadedFile,
  updateBillingAddress,
  updateShippingAddress,
  clearUploadPO,
  setCustomerList,
  setOrderResponseProps,
  setNetworkList,
  setAccountDropdown,
  setActiveOrders,
  updateEntireCart,
  setCountryList,
  setProductSubTypes,
  setProductSubCategoriesList,
  resetOrderResponseProps,
  resetMarketplaceFilters,
  resetCartResponse,
  setUserPersona,
  setAgreementDetails,
  setAgreementValidity,
  removeProductItem,
  // Change the crosser selection
  setCrosserPackagesSelection,
  setTermsAcceptance,
  addMultipleProductToCart,
  updateAdditionalInputs,
  clearAdditionalInputs,
  removeMultipleProductsFromCart,
  setMarketplaceSid,
  setConfiguratorFields,
  setBundleOfProductDetails,
  updateMxieEdgeServer,
  setAdditionalInputs,
  setBundleOfProductItems,
  // Stripe payment POC
  setPaymentOption,
  setStripeSessionId,
  setStripeOfferId,
  setPaymentOutcome,
  setStripeConfig,
  setPendingStripeReturn,
  setStripeRedirectHint,
  resetPaymentState,
} = marketplaceSlice.actions;